import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { toast } from "sonner";
import ReadAloud from "@/components/ReadAloud";
import useSEO from "@/lib/useSEO";
import { trackApplyNow } from "@/lib/analytics";
import * as Icons from "lucide-react";
import { MapPin, Users, IndianRupee, Phone, ArrowLeft, BadgeCheck, TrendingUp, X } from "lucide-react";

const CATEGORY_META = {
  welder: "Flame", fitter: "Wrench", electrician: "Zap", plumber: "Droplets",
  rigger: "Anchor", technician: "Cog", helper: "HandHelping", mason: "Hammer",
};

// Google JobPosting schema — unlocks the Google Jobs vertical
function buildJobPostingJsonLd(job) {
  if (!job) return null;
  return {
    "@context": "https://schema.org",
    "@type": "JobPosting",
    "title": job.title,
    "description": job.description,
    "identifier": { "@type": "PropertyValue", "name": "Sarthi Associates", "value": job.id },
    "datePosted": job.created_at,
    "employmentType": "CONTRACTOR",
    "hiringOrganization": {
      "@type": "Organization",
      "name": job.contractor_name || "Sarthi Associates Partner",
      "sameAs": "https://sarthiassociates.com",
      "logo": "https://sarthiassociates.com/sarthi-logo.png",
    },
    "jobLocation": {
      "@type": "Place",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": job.location,
        "addressCountry": "IN",
      },
    },
    "baseSalary": {
      "@type": "MonetaryAmount",
      "currency": "INR",
      "value": {
        "@type": "QuantitativeValue",
        "minValue": job.salary_min,
        "maxValue": job.salary_max,
        "unitText": "MONTH",
      },
    },
    "industry": "Industrial / Construction",
    "occupationalCategory": job.category,
    "directApply": true,
    "url": `https://sarthiassociates.com/jobs/${job.id}`,
  };
}

export default function JobDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const [job, setJob] = useState(null);
  const [applied, setApplied] = useState(false);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [salary, setSalary] = useState(null);
  const [ctcOpen, setCtcOpen] = useState(false);
  const [lastAppId, setLastAppId] = useState(null);

  useEffect(() => {
    api.get(`/jobs/${id}`).then((r) => setJob(r.data)).catch(() => nav("/jobs"));
    if (user?.role === "worker") {
      api.get("/applications/mine").then((r) => setApplied(!!r.data.find((a) => a.job_id === id))).catch(() => {});
    }
  }, [id, user]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!job) return;
    const city = (job.location || "").split(",")[0].trim();
    api.get(`/stats/salary`, { params: { trade: job.category, city } })
      .then((r) => setSalary(r.data))
      .catch(() => setSalary(null));
  }, [job]);

  useSEO(
    job ? {
      title: `${job.title} — ${job.location} | Sarthi Associates`,
      description: (job.description || "").slice(0, 200) + " Apply now on Sarthi Associates — India's frontline workforce portal.",
      path: `/jobs/${job.id}`,
      jsonLd: buildJobPostingJsonLd(job),
    } : { title: "Job Details | Sarthi Associates", description: "Frontline job details on Sarthi Associates.", path: `/jobs/${id}` }
  );

  const apply = async () => {
    if (!user) { toast.error("Please login first"); return nav("/login"); }
    if (user.role !== "worker") return toast.error("Only workers can apply");
    setBusy(true);
    try {
      trackApplyNow(id, job?.category);
      const { data: appData } = await api.post(`/jobs/${id}/apply`, { message: msg });
      setApplied(true);
      setLastAppId(appData?.id || appData?.application_id || null);
      setCtcOpen(true);
      toast.success(t("applied_success"));
    } catch (e) { toast.error(formatError(e)); }
    finally { setBusy(false); }
  };

  if (!job) return <div className="p-10 text-center text-sarthi-muted">Loading…</div>;
  const Icon = Icons[CATEGORY_META[job.category] || "Wrench"];

  const fullText = `${job.title}. ${job.description}. Location ${job.location}. Salary ${job.salary_min} to ${job.salary_max} rupees. ${job.positions} positions.`;

  return (
    <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6">
      <button onClick={() => nav(-1)} data-testid="back-btn" className="mb-4 inline-flex items-center gap-2 text-sm font-bold text-sarthi-muted hover:text-sarthi-primary"><ArrowLeft className="h-4 w-4"/> {t("back")}</button>
      <div className="sarthi-card p-6 sm:p-8">
        <div className="flex items-start justify-between gap-4">
          <div className={`flex h-16 w-16 items-center justify-center rounded-2xl cat-${["orange","green","yellow","blue","stone","indigo","purple","amber"][["welder","fitter","electrician","plumber","rigger","technician","helper","mason"].indexOf(job.category)] || "orange"}`}>
            <Icon className="h-10 w-10" strokeWidth={2.5} />
          </div>
          <ReadAloud text={fullText} />
        </div>
        <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight sm:text-4xl">{job.title}</h1>
        <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-2 text-base font-semibold text-sarthi-muted">
          <span className="inline-flex items-center gap-2"><MapPin className="h-5 w-5" /> {job.location}</span>
          <span className="inline-flex items-center gap-2"><Users className="h-5 w-5" /> {job.positions} {t("positions")}</span>
          <span className="inline-flex items-center gap-2 font-extrabold text-sarthi-secondary"><IndianRupee className="h-5 w-5" /> ₹{job.salary_min} – ₹{job.salary_max}</span>
          {job.contact_phone && <span className="inline-flex items-center gap-2"><Phone className="h-5 w-5" /> {job.contact_phone}</span>}
        </div>
        <div className="mt-5 rounded-xl bg-sarthi-accent p-4 text-base leading-relaxed text-sarthi-text">
          {job.description}
        </div>

        {job.long_description && (
          <div className="mt-4 rounded-xl border-2 border-sarthi-primary/30 bg-orange-50 p-4 text-base leading-relaxed text-sarthi-text">
            <div className="mb-1 text-xs font-bold uppercase tracking-wide text-sarthi-primary">About this site</div>
            {job.long_description}
          </div>
        )}

        {Array.isArray(job.site_photo_ids) && job.site_photo_ids.length > 0 && (
          <div className="mt-5">
            <div className="mb-2 text-xs font-bold uppercase tracking-wide text-sarthi-muted">Site photos</div>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {job.site_photo_ids.map((fid) => (
                <a
                  key={fid}
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${fid}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`site-photo-${fid}`}
                  className="overflow-hidden rounded-xl border-2 border-sarthi-border"
                >
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${fid}`}
                    alt="Job site"
                    className="aspect-video w-full object-cover transition-transform hover:scale-105"
                  />
                </a>
              ))}
            </div>
          </div>
        )}

        <div className="mt-6 text-sm font-semibold text-sarthi-muted flex items-center gap-2">
          Posted by: {job.contractor_name || "Contractor"}
          {job.contractor_verified && (
            <span
              data-testid="verified-recruiter-badge"
              title="Verified Recruiter"
              className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-bold text-emerald-700 ring-1 ring-emerald-200"
            >
              <BadgeCheck className="h-3.5 w-3.5" /> Verified
            </span>
          )}
        </div>
        {salary?.median && (
          <div
            data-testid="salary-insights-badge"
            className="mt-4 inline-flex items-center gap-2 rounded-xl border-2 border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-bold text-emerald-800"
          >
            <TrendingUp className="h-4 w-4" />
            Median {job.category} salary in {(job.location || "").split(",")[0].trim()}:
            <span className="text-emerald-900">₹{Number(salary.median).toLocaleString("en-IN")}/day</span>
            <span className="ml-1 font-medium text-emerald-700/80">({salary.sample_size} jobs)</span>
          </div>
        )}
      </div>

      {user?.role === "worker" && !applied && (
        <div className="sarthi-card mt-6 p-6">
          <label className="mb-1 block text-sm font-bold uppercase tracking-wide text-sarthi-muted">Message (optional)</label>
          <textarea data-testid="apply-msg" value={msg} onChange={(e) => setMsg(e.target.value)} className="w-full rounded-xl border-2 border-sarthi-border bg-white px-4 py-3 text-base font-semibold focus:border-sarthi-primary focus:outline-none" rows={3} />
          <button data-testid="apply-btn" onClick={apply} disabled={busy} className="sarthi-btn-primary mt-4 w-full py-4 text-lg text-white disabled:opacity-50">
            {t("apply_now")}
          </button>
        </div>
      )}

      {applied && (
        <div className="sarthi-card mt-6 p-6 text-center">
          <div className="text-lg font-extrabold text-green-700">✓ {t("applied")}</div>
        </div>
      )}

      {!user && (
        <div className="sarthi-card mt-6 p-6 text-center">
          <button onClick={() => nav("/login")} data-testid="login-to-apply" className="sarthi-btn-primary w-full py-4 text-lg text-white">{t("login")} → {t("apply_now")}</button>
        </div>
      )}

      {ctcOpen && (
        <CtcExtraModal
          applicationId={lastAppId}
          onClose={() => setCtcOpen(false)}
        />
      )}
    </div>
  );
}

function CtcExtraModal({ applicationId, onClose }) {
  const [form, setForm] = useState({ current_location: "", current_company: "", current_ctc: "", expected_ctc: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));
  const submit = async () => {
    if (!applicationId) { onClose(); return; }
    setBusy(true);
    try {
      await api.post("/applications/ctc-extra", { application_id: applicationId, ...form });
      toast.success("Thanks! Your profile is stronger now.");
      onClose();
    } catch (e) { toast.error(formatError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose} data-testid="ctc-extra-modal">
      <div className="sarthi-card w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 flex items-start justify-between gap-3">
          <div>
            <h2 className="font-display text-xl font-extrabold text-sarthi-text">One quick step (optional)</h2>
            <p className="mt-1 text-xs font-medium text-sarthi-muted">Recruiters shortlist ~3x faster when they see your CTC and current employer. This does not block your application.</p>
          </div>
          <button onClick={onClose} className="rounded-lg p-1 hover:bg-sarthi-accent" data-testid="ctc-close"><X className="h-5 w-5" /></button>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <CtcField testid="ctc-location" label="Current location" value={form.current_location} onChange={set("current_location")} placeholder="Delhi" />
          <CtcField testid="ctc-company" label="Current company" value={form.current_company} onChange={set("current_company")} placeholder="ABC Pvt Ltd" />
          <CtcField testid="ctc-current" label="Current CTC / day" value={form.current_ctc} onChange={set("current_ctc")} placeholder="₹700" inputMode="numeric" />
          <CtcField testid="ctc-expected" label="Expected CTC / day" value={form.expected_ctc} onChange={set("expected_ctc")} placeholder="₹900" inputMode="numeric" />
        </div>
        <div className="mt-5 flex gap-2">
          <button data-testid="ctc-skip" onClick={onClose} className="sarthi-btn-outline flex-1 py-3 font-bold">Skip &amp; Apply</button>
          <button data-testid="ctc-submit" onClick={submit} disabled={busy} className="sarthi-btn-primary flex-1 py-3 text-white disabled:opacity-50">
            {busy ? "Saving…" : "Save details"}
          </button>
        </div>
      </div>
    </div>
  );
}

function CtcField({ label, value, onChange, placeholder, inputMode, testid }) {
  return (
    <div>
      <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-sarthi-muted">{label}</label>
      <input
        data-testid={testid}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        inputMode={inputMode}
        className="w-full rounded-xl border-2 border-sarthi-border bg-white px-3 py-2 text-sm font-semibold focus:border-sarthi-primary focus:outline-none"
      />
    </div>
  );
}
